import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { getOLARouting } from "../Service/partnerAPI";
import { getOlaPublicBrowserKey } from "../Service/OlaMapsService";
import {
  joinTrackingRoom,
  leaveTrackingRoom,
  onLocationUpdate,
  onStatusUpdate,
} from "../Service/SocketService";

const LiveTrackingMap = ({
  visitID,
  patientLocation,
  initialHCPLocation,
  hcpName,
  hcpRole,
  onClose,
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const hcpMarkerRef = useRef(null);
  const [olaKey, setOlaKey] = useState("");
  const [isLoadingMapConfig, setIsLoadingMapConfig] = useState(true);
  const [error, setError] = useState(null);
  const [routingDetails, setRoutingDetails] = useState({
    arrivalClockTime: null,
    readableDistance: null,
  });

  useEffect(() => {
    let isActive = true;

    const loadMapConfig = async () => {
      const publicBrowserKey = await getOlaPublicBrowserKey();
      if (!isActive) return;

      setOlaKey(publicBrowserKey);
      setError(
        publicBrowserKey
          ? null
          : "Map configuration missing on the server. Set OLA_API_KEY.",
      );
      setIsLoadingMapConfig(false);
    };

    loadMapConfig();
    return () => {
      isActive = false;
    };
  }, []);

  const fetchRoute = async (hcpCoords) => {
    if (!olaKey || !patientLocation || !hcpCoords) return null;

    try {
      const origin = `${hcpCoords.lat},${hcpCoords.lng}`;
      const destination = `${patientLocation.lat},${patientLocation.lng}`;
      // console.log("Fetching route from proxy:", { origin, destination });
      const res = await getOLARouting(origin, destination);
      // console.log("Route proxy response:", res);

      if (
        res.error === false &&
        res.data.routes &&
        res.data.routes.length > 0
      ) {
        const route = res.data.routes[0];
        const leg = route.legs?.[0];

        // Update ETA/Distance state
        if (leg) {
          const arrivalTime = moment()
            .add(leg.duration + 300, "seconds")
            .format("hh:mm A");
          setRoutingDetails({
            arrivalClockTime: arrivalTime,
            readableDistance: leg.readable_distance,
          });
        }

        // Support both { points: "..." } and direct string formats
        const points =
          typeof route.overview_polyline === "string"
            ? route.overview_polyline
            : route.overview_polyline?.points;

        // console.log("Successfully extracted route points:", points ? "Yes (length: " + points.length + ")" : "No");
        return points;
      } else {
        console.warn("Route not found or proxy error:", res);
      }
    } catch (err) {
      console.error("Error fetching route via proxy:", err);
    }
    return null;
  };

  // Helper to decode Ola/Google polyline
  const decodePolyline = (encoded) => {
    if (!encoded) return [];
    let poly = [];
    let index = 0,
      len = encoded.length;
    let lat = 0,
      lng = 0;

    while (index < len) {
      let b,
        shift = 0,
        result = 0;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      let dlat = result & 1 ? ~(result >> 1) : result >> 1;
      lat += dlat;

      shift = 0;
      result = 0;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      let dlng = result & 1 ? ~(result >> 1) : result >> 1;
      lng += dlng;

      poly.push([lng / 1e5, lat / 1e5]);
    }
    return poly;
  };

  const updateTrackingLine = async (map, hcpCoords) => {
    if (!map || !patientLocation || !hcpCoords) return;

    if (!map.isStyleLoaded()) {
      map.once("load", () => {
        updateTrackingLine(map, hcpCoords).catch((error) => {
          console.error("Deferred tracking line update failed:", error);
        });
      });
      return;
    }

    let coordinates = [
      [patientLocation.lng, patientLocation.lat],
      [hcpCoords.lng, hcpCoords.lat],
    ];

    const encodedRoute = await fetchRoute(hcpCoords);
    if (encodedRoute) {
      coordinates = decodePolyline(encodedRoute);
      //   console.log("Decoded route coordinates count:", coordinates.length);
    }

    const geojson = {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: coordinates,
      },
    };

    if (map.getSource("tracking-line")) {
      map.getSource("tracking-line").setData(geojson);
    } else {
      map.addSource("tracking-line", {
        type: "geojson",
        data: geojson,
      });

      map.addLayer({
        id: "tracking-line",
        type: "line",
        source: "tracking-line",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#4361EE",
          "line-width": 5,
          "line-opacity": 0.8,
        },
      });
    }
  };

  const createOrUpdateHCPMarker = async (map, lat, lng) => {
    if (!hcpMarkerRef.current) {
      // Create HCP Marker
      const el = document.createElement("div");
      el.className = "hcp-marker";
      el.style.width = "32px";
      el.style.height = "32px";
      el.style.backgroundColor = "#4361EE";
      el.style.borderRadius = "50%";
      el.style.border = "2px solid white";
      el.style.boxShadow = "0 0 10px rgba(0,0,0,0.3)";
      el.style.display = "flex";
      el.style.alignItems = "center";
      el.style.justifyContent = "center";
      el.style.cursor = "pointer";
      el.innerHTML = `
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="18.5" cy="17.5" r="3.5"></circle>
                    <circle cx="5.5" cy="17.5" r="3.5"></circle>
                    <circle cx="15" cy="5" r="1"></circle>
                    <path d="M12 17.5V14l-3-3 4-3 2 3h2"></path>
                </svg>
            `;

      hcpMarkerRef.current = new maplibregl.Marker({ element: el })
        .setLngLat([lng, lat])
        .setPopup(
          new maplibregl.Popup({ offset: 25 }).setHTML(
            `<b>${hcpName || "HCP"}</b>`,
          ),
        )
        .addTo(map);
    } else {
      // Update HCP Marker Position
      hcpMarkerRef.current.setLngLat([lng, lat]);
    }

    // Update the connecting line (AWAIT THIS AS IT FETCHES ROUTE)
    await updateTrackingLine(map, { lat, lng });
  };

  useEffect(() => {
    if (!olaKey || !mapContainerRef.current) return;

    // Initialize Map
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: `https://api.olamaps.io/tiles/vector/v1/styles/default-light-standard/style.json`,
      center: [patientLocation.lng, patientLocation.lat],
      zoom: 14,
      transformRequest: (url, resourceType) => {
        const mapUrl = new URL(url);
        if (mapUrl.host === "api.olamaps.io") {
          if (!mapUrl.searchParams.has("api_key")) {
            mapUrl.searchParams.append("api_key", olaKey);
          }
          return { url: mapUrl.toString(), resourceType };
        }
        return { url, resourceType };
      },
    });

    mapRef.current = map;

    map.on("load", async () => {
      // Add Patient Marker (Fixed)
      const pEl = document.createElement("div");
      pEl.style.backgroundColor = "#EF4444";
      pEl.style.width = "32px";
      pEl.style.height = "32px";
      pEl.style.borderRadius = "50%";
      pEl.style.border = "2px solid white";
      pEl.style.display = "flex";
      pEl.style.alignItems = "center";
      pEl.style.justifyContent = "center";
      pEl.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";

      pEl.innerHTML = `
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
            `;

      new maplibregl.Marker({ element: pEl })
        .setLngLat([patientLocation.lng, patientLocation.lat])
        .setPopup(
          new maplibregl.Popup({ offset: 25 }).setHTML(
            "<b>Patient Location</b>",
          ),
        )
        .addTo(map);

      // Add Initial HCP Marker if available
      if (
        initialHCPLocation &&
        initialHCPLocation.lat &&
        initialHCPLocation.lng
      ) {
        await createOrUpdateHCPMarker(
          map,
          initialHCPLocation.lat,
          initialHCPLocation.lng,
        );

        // Initial fit bounds
        const bounds = new maplibregl.LngLatBounds()
          .extend([patientLocation.lng, patientLocation.lat])
          .extend([initialHCPLocation.lng, initialHCPLocation.lat]);
        map.fitBounds(bounds, { padding: 80, maxZoom: 15 });
      }
    });

    map.on("error", (e) => {
      const errorMessage = e.error?.message || e.message || "";
      // Ignore the "3d_model" missing layer error as it's a known style issue with Ola Maps
      if (errorMessage.includes("3d_model")) return;

      console.error("MapLibre error:", e);
      if (e.error && e.error.status === 403) {
        setError(
          "Map access denied (403). Please check if the API key is restricted to this domain.",
        );
      }
    });

    // Socket Connection
    const cancelPendingRoomJoin = joinTrackingRoom(visitID);

    const removeLocationUpdate = onLocationUpdate(async (data) => {
      // console.log("HCP Location Update received in KPI:", data);
      if (String(data?.visitID || "") === String(visitID) && data?.coords) {
        const { lat, lng } = data.coords;
        await createOrUpdateHCPMarker(map, lat, lng);

        // Check if marker is outside current bounds before refitting
        const currentBounds = map.getBounds();
        if (!currentBounds.contains([lng, lat])) {
          const bounds = new maplibregl.LngLatBounds()
            .extend([patientLocation.lng, patientLocation.lat])
            .extend([lng, lat]);
          map.fitBounds(bounds, { padding: 80, maxZoom: 15, duration: 1000 });
        }
      }
    });

    const removeStatusUpdate = onStatusUpdate((data) => {
      // console.log("Status update received in tracking:", data);
      if (data && data.visitID === visitID && data.liveStatus !== "arriving") {
        console.log("HCP no longer arriving, closing tracking map.");
        onClose();
      }
    });

    return () => {
      cancelPendingRoomJoin();
      removeLocationUpdate();
      removeStatusUpdate();
      leaveTrackingRoom(visitID);
      map.remove();
    };
  }, [olaKey]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="p-4 border-b flex justify-between items-center bg-gray-50">
          <div>
            <h3 className="text-lg font-bold text-gray-800 font-poppins">
              Live Tracking
            </h3>
            <p className="text-sm text-gray-500 font-poppins">
              Care Provider:{" "}
              <span className="text-[#4361EE] font-medium">
                {hcpName || "HCP"}
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <svg
              className="w-6 h-6 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 relative">
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 text-red-500 font-poppins">
              {error}
            </div>
          ) : isLoadingMapConfig || !olaKey ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 gap-4">
              <div className="animate-spin h-10 w-10 border-4 border-[#4361EE] border-t-transparent rounded-full"></div>
              <p className="text-gray-500 font-poppins">
                Initializing Live Map...
              </p>
            </div>
          ) : null}
          <div ref={mapContainerRef} className="w-full h-full" />

          {routingDetails.arrivalClockTime && (
            <div className="absolute top-4 left-4 z-10 bg-white p-3 rounded-xl shadow-lg border border-gray-100 flex flex-col gap-2 animate-in slide-in-from-left-4 duration-500 overflow-hidden">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-50 rounded-lg">
                  <svg
                    className="w-4 h-4 text-[#4361EE]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider leading-none">
                    Est. Arrival
                  </p>
                  <p className="text-sm font-bold text-gray-800 font-poppins">
                    {routingDetails.arrivalClockTime}
                  </p>
                </div>
              </div>
              <div className="w-full h-px bg-gray-50" />
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-green-50 rounded-lg">
                  <svg
                    className="w-4 h-4 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider leading-none">
                    Distance
                  </p>
                  <p className="text-sm font-semibold text-gray-600 font-poppins">
                    {routingDetails.readableDistance} away
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 bg-gray-50 border-t flex items-center justify-between text-xs text-gray-500 font-poppins">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
              </div>
              <span>Patient's home</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[#4361EE] flex items-center justify-center">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="18.5" cy="17.5" r="3.5"></circle>
                  <circle cx="5.5" cy="17.5" r="3.5"></circle>
                  <circle cx="15" cy="5" r="1"></circle>
                  <path d="M12 17.5V14l-3-3 4-3 2 3h2"></path>
                </svg>
              </div>
              <span>{hcpName || "HCP"} current location</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[#4361EE] font-medium">
            <div className="w-1.5 h-1.5 rounded-full bg-[#4361EE] animate-pulse" />
            <span className="capitalize">
              {(hcpRole || "HCP")?.toLowerCase()} is on the way
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveTrackingMap;
